<template>
  <div id="app">
    <v-container>
      <Header />
      <v-overlay :value="loader">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-snackbar
        v-model="message.snackbar"
        :timeout="message.snackbarTimeout"
        bottom
      >
        {{ message.snackbarText }}
        <v-btn dark text @click="message.snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-dialog v-model="createCompanyDialog" scrollable max-width="500px">
        <v-card>
          <v-card-title>Create company</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-alert type="error" class="mt-5" v-if="showCompanyMessage" dense
              >Company already exists</v-alert
            >
            <v-form ref="createCompanyForm">
              <v-text-field
                v-model="companyForm.name"
                :rules="[rules.required]"
                label="Company"
                required
              ></v-text-field>
              <v-text-field
                v-model="companyForm.email"
                :rules="[rules.required, rules.email]"
                label="Email"
                required
              ></v-text-field>
              <v-file-input
                v-model="companyForm.logo"
                show-size
                label="File"
              ></v-file-input>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn
              color="blue darken-1"
              text
              @click="createCompanyDialog = false"
              class="hp--btn"
              >Close</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              :loading="createCompanyLoader"
              @click="onCreateCompany()"
              class="hp--btn"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="updateCompanyDialog" scrollable max-width="500px">
        <v-card>
          <v-card-title>Update company</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="updateForm">
              <v-text-field
                v-model="companyUpdateForm.name"
                :rules="[rules.required]"
                label="Company"
                required
              ></v-text-field>
              <v-text-field
                v-model="companyUpdateForm.email"
                :rules="[rules.required, rules.email]"
                label="Email"
                required
              ></v-text-field>
              <v-file-input
                v-model="companyUpdateForm.logo"
                show-size
                label="File"
              ></v-file-input>
              <v-chip
                class="mr-2"
                close
                color="primary"
                align="right"
                v-if="fileUploadChip"
                @click:close="deleteCompanyAttachment(companyUpdateForm)"
              >
                <v-icon left>mdi-file-document</v-icon>
                {{ companyUpdateForm.filename }}
              </v-chip>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn
              color="blue darken-1"
              text
              @click="updateCompanyDialog = false"
              class="hp--btn"
              >Close</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              @click="onUpdateCompany()"
              :loading="updateCompanyLoader"
              class="hp--btn"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div style="position: relative" v-if="switchEventPanel === 'companies'">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title class="grey--text">Companies</v-toolbar-title>
            <div class="flex-grow-1"></div>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
            ></v-text-field>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  dark
                  small
                  absolute
                  bottom
                  left
                  fab
                  v-on="on"
                  @click="
                    companyForm = [];
                    createCompanyDialog = !createCompanyDialog;
                  "
                  class="hp--btn"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>New company</span>
            </v-tooltip>
          </v-toolbar>
          <v-divider></v-divider>

          <v-card-text>
            <v-data-table
              :items="companys"
              :headers="companysHeaders"
              class="elevation-1"
              :page.sync="page"
              :items-per-page="itemsPerPage"
              :search="search"
              :sort-by="['name']"
            >
              <template v-slot:item.action="{ item }">
                <v-chip
                  color="red lighten-4"
                  class="ml-0 mr-2 black--text"
                  label
                  small
                  >{{ item.active }}/{{ item.total }} active</v-chip
                >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      color="success"
                      @click="updateCompany(item)"
                      >edit</v-icon
                    >
                  </template>
                  <span>Update company</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="deleteCompany(item)" color="error"
                      >delete</v-icon
                    >
                  </template>
                  <span>Delete company</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      @click="viewClientAccess(item)"
                      color="secondary"
                      >group_add</v-icon
                    >
                  </template>
                  <span>Add client access</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="viewLocations(item)" color="info"
                      >mdi-home</v-icon
                    >
                  </template>
                  <span>View locations</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      class="mr-1"
                      @click="viewEvents(item)"
                      color="warning"
                      >mdi-bus</v-icon
                    >
                  </template>
                  <span>View events</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </div>

      <company-locations
        v-if="switchEventPanel === 'locations'"
        @switchEventPanel="childMessageReceived"
        :company="company"
      />

      <company-client-access
        v-if="switchEventPanel === 'clientaccess'"
        @switchEventPanel="childMessageReceived"
        :company="company"
      />

      <company-events
        v-if="switchEventPanel === 'events'"
        @switchEventPanel="childMessageReceived"
        :company="company"
      />
    </v-container>
  </div>
</template>

<script>
import Header from "../components/Header.vue";
import { Company } from "../classes/Company";
import CompanyLocations from "../components/CompanyLocations.vue";
import CompanyEvents from "../components/CompanyEvents.vue";
import CompanyClientAccess from "../components/CompanyClientAccess.vue";
import moment from "moment";
import { API } from "aws-amplify";
import _ from "lodash";

export default {
  components: {
    Header,
    CompanyLocations,
    CompanyEvents,
    CompanyClientAccess
  },
  data() {
    return {
      createCompanyDialog: false,
      createCompanyLoader: false,
      updateCompanyDialog: false,
      updateCompanyLoader: false,
      switchCompanyEvent: true,
      showCompanyMessage: false,
      showCompanyUpdateMessage: false,
      loader: false,
      switchEventPanel: "companies",
      companys: [],
      companysHeaders: [
        {
          text: "Name",
          align: "left",
          value: "name"
        },
        { text: "Created", value: "date" },
        { text: "Email", value: "email" },
        { value: "action", align: "right", sortable: false, width: 300 }
      ],
      company: [],
      companyForm: [],
      companyUpdateForm: [],
      fileUploadChip: false,
      rules: {
        required: v => !!v || "This field is required.",
        email: v => /.+@.+\..+/.test(v) || "E-mail must be valid"
      },
      message: {
        snackbar: false,
        snackbarText: "",
        snackbarTimeout: 3000
      },
      itemsPerPage: -1,
      page: 0,
      nextToken: "",
      search: ""
    };
  },
  async created() {
    const that = this;
    const listEventBookings = /* GraphQL */ `
      query ListEventBookings(
        $filter: ModelEventBookingFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listEventBookings(
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            id
            firstname
            lastname
            email
            mobile
            dob
            customData
            createdAt
            startTime
            twentyFourHourReminder
            fortyEightHourReminder
            attendance
            searchField
            isDeleted
            updatedAt
            eventSlot {
              id
              time
              length
              type
              booked
              isDeleted
              createdAt
              updatedAt
              eventSlotGroup {
                isDeleted
                active
              }
            }
          }
          nextToken
        }
      }
    `;
    const dayStart = moment()
      .add(1, "days")
      .startOf("day")
      .toISOString();
    const dayEnd = moment()
      .add(1, "days")
      .endOf("day")
      .toISOString();

    let {
      data: { listEventBookings: results }
    } = await API.graphql({
      query: listEventBookings,
      variables: {
        filter: {
          startTime: { between: [dayStart, dayEnd] }
          //twentyFourHourReminder: { ne: true },
        },
        nextToken: null
      }
    });

    let bookings = results.items;
    let nextToken = results.nextToken;

    while (nextToken !== null) {
      let {
        data: { listEventBookings: results }
      } = await API.graphql({
        query: listEventBookings,
        variables: {
          filter: {
            startTime: { between: [dayStart, dayEnd] },
            twentyFourHourReminder: { ne: true }
          },
          limit: 1000,
          nextToken: nextToken
        }
      });
      nextToken = results.nextToken;
      bookings.push(...results.items);
    }

    this.loader = true;

    let nextCompanys = await new Company().listCompanys(null);
    let companys = nextCompanys.items;
    this.nextToken = nextCompanys.nextToken;

    while (this.nextToken !== null) {
      let nextCompanys = await new Company().listCompanys(this.nextToken);
      this.nextToken = nextCompanys.nextToken;
      companys.push(...nextCompanys.items);
    }

    companys.forEach(function(company) {
      if (company.isDeleted === 0) {
        var totalActive = company.event.items.filter(function(item) {
          return item.status === "Live";
        });
        that.companys.push({
          id: company.id,
          name: company.name,
          email: company.email,
          logo: company.logo,
          date: moment(company.createdAt).format("DD/MM/YYYY"),
          active: totalActive.length,
          total: company.event.items.length
        });
      }
    });
    this.loader = false;
  },
  methods: {
    childMessageReceived(arg1) {
      this.switchEventPanel = arg1;
    },
    viewEvents(company) {
      this.company = company;
      this.switchCompanyEvent = !this.switchCompanyEvent;
      this.switchEventPanel = "events";
    },
    viewLocations(company) {
      this.company = company;
      this.switchCompanyEvent = !this.switchCompanyEvent;
      this.switchEventPanel = "locations";
    },
    viewClientAccess(company) {
      this.company = company;
      this.switchEventPanel = "clientaccess";
    },
    async onCreateCompany() {
      const that = this;
      if (that.$refs.createCompanyForm.validate()) {
        that.createCompanyLoader = true;
        that.showCompanyMessage = false;
        try {
          const company = await new Company().createCompany(this.companyForm);
          that.companys.push({
            id: company.id,
            name: company.name.trim(),
            email: company.email.trim(),
            date: moment(company.createdAt).format("DD/MM/YYYY"),
            isDeleted: company.isDeleted,
            active: 0,
            total: 0
          });
          that.companys = _.orderBy(that.companys, "name", "asc");
          that.createCompanyDialog = false;
          that.createCompanyLoader = false;
          that.showSuccessMessage("New company " + company.name + " created");
        } catch (e) {
          that.showCompanyMessage = true;
          that.createCompanyLoader = false;
        }
      }
    },
    async onUpdateCompany() {
      const that = this;
      if (this.$refs.updateForm.validate()) {
        this.showCompanyUpdateMessage = false;

        try {
          const companyUpdated = await new Company().updateCompany(
            this.companyUpdateForm
          );
          this.companys.forEach(function(company) {
            if (company.id === companyUpdated.id) {
              company.name = companyUpdated.name;
              company.email = companyUpdated.email;
            }
          });
          this.updateCompanyLoader = false;
          this.updateCompanyDialog = false;
          this.showSuccessMessage("Company updated");
        } catch (e) {
          that.showCompanyMessage = true;
          that.createCompanyLoader = false;
        }
      }
    },
    async updateCompany(company) {
      const result = await new Company().getCompany(company);
      this.companyUpdateForm = {
        id: result.id,
        name: result.name,
        email: result.email,
        filename: result.logoFileName
      };
      this.fileUploadChip = result.logoFileName !== null ? true : false;
      this.updateCompanyDialog = !this.updateCompanyDialog;
    },
    async deleteCompany(company) {
      if (confirm("Are you sure you want to delete this company?")) {
        await new Company().deleteCompany(company);
        this.companys.splice(this.companys.indexOf(company), 1);
        this.showSuccessMessage("Company deleted");
      }
    },
    async deleteCompanyAttachment(company) {
      if (confirm("Are you sure you want to delete company logo?")) {
        const result = await new Company().deleteCompanyAttachment(company);
        if (result) {
          this.fileUploadChip = false;
        }
      }
    },
    showSuccessMessage(message) {
      this.message.snackbarText = message;
      this.message.snackbar = true;
    }
  }
};
</script>
